import { WageFrequency } from "./enums";

const CENTS_PER_DOLLAR = 100n;
const MINUTES_PER_HOUR = 60n;
const MS_PER_MINUTE = 60_000;
// Payables record hours with 4 decimals.
const QUANTITY_SCALE = 10_000n;
const ONE_UNIT_QUANTITY = "1.0000";
// Negative wages are provider-side corrections.
const MONEY_PATTERN = /^(-?)(\d+)(?:\.(\d{1,2}))?$/;

export type TimeEntryPay = {
  // Worked minutes, counting any partial minute as a whole one.
  workedMinutes: number;
  // The payable's quantity: hours with 4 decimals for hourly pay, else one unit.
  quantity: string;
  // Dollars with 2 decimals.
  amount: string;
};

function toCents(amount: string): bigint {
  const match = MONEY_PATTERN.exec(amount.trim());
  if (!match) {
    throw new RangeError(`Invalid wage amount: ${amount}`);
  }
  const [, sign, dollars, fraction = ""] = match;
  const cents =
    BigInt(dollars) * CENTS_PER_DOLLAR + BigInt(fraction.padEnd(2, "0"));
  return sign ? -cents : cents;
}

function formatScaled(
  value: bigint,
  { scale, digits }: { scale: bigint; digits: number },
): string {
  const sign = value < 0n ? "-" : "";
  const magnitude = value < 0n ? -value : value;
  const fraction = (magnitude % scale).toString().padStart(digits, "0");
  return `${sign}${magnitude / scale}.${fraction}`;
}

/**
 * Rounds toward positive infinity, like Postgres ceil in the payable amount
 * check: a correction of -$733.333... becomes -$733.33.
 */
function divideRoundingUp(numerator: bigint, denominator: bigint): bigint {
  // BigInt division truncates toward zero, which already rounds negatives up.
  if (numerator < 0n) {
    return numerator / denominator;
  }
  return (numerator + denominator - 1n) / denominator;
}

function divideRoundingHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/**
 * What a time entry pays at its wage. Hourly pay is the wage times the worked
 * minutes divided by 60, rounded up to the next cent, so a provider is never
 * paid less than the exact amount. Any other frequency pays the wage once.
 *
 * The API prices approvals and the web client previews Add Hours with this one
 * function, using integer math, so both arrive at the same cent. Minutes are
 * the unit because 4-decimal hours can't represent most minute counts exactly
 * (20 minutes is 0.3333... hours); the database check rebuilds whole minutes
 * from the recorded quantity to verify the amount.
 */
export function calculateTimeEntryPay({
  unitAmount,
  wageFrequencyId,
  startsAt,
  endsAt,
}: {
  unitAmount: string;
  wageFrequencyId: WageFrequency;
  startsAt: Date;
  endsAt: Date;
}): TimeEntryPay {
  const durationMs = endsAt.getTime() - startsAt.getTime();
  if (durationMs <= 0) {
    throw new RangeError("A time entry must end after it starts");
  }
  const workedMinutes = Math.ceil(durationMs / MS_PER_MINUTE);
  const unitCents = toCents(unitAmount);

  if (wageFrequencyId !== WageFrequency.Hourly) {
    return {
      workedMinutes,
      quantity: ONE_UNIT_QUANTITY,
      amount: formatScaled(unitCents, { scale: CENTS_PER_DOLLAR, digits: 2 }),
    };
  }

  const minutes = BigInt(workedMinutes);
  const quantity = divideRoundingHalfUp(
    minutes * QUANTITY_SCALE,
    MINUTES_PER_HOUR,
  );
  const amountCents = divideRoundingUp(unitCents * minutes, MINUTES_PER_HOUR);
  return {
    workedMinutes,
    quantity: formatScaled(quantity, { scale: QUANTITY_SCALE, digits: 4 }),
    amount: formatScaled(amountCents, { scale: CENTS_PER_DOLLAR, digits: 2 }),
  };
}
